import { createTheme } from '@mui/material/styles';

const ensightfulPalette = {
    neutral: {
        50: '#fafafb',
        100: '#f0f0f2',
        200: '#e2e1e5',
        300: '#d3d2d7',
        400: '#bfbec6',
        500: '#9d9ba7',
        600: '#787585',
        700: '#56545f',
        800: '#333239',
        900: '#222126',
    },
    primary: {
        50: '#efedff',
        100: '#d3ccff',
        200: '#b7abff',
        300: '#9b89ff',
        400: '#7f68ff',
        500: '#6347ff',
        600: '#523ee0',
        700: '#4136c0',
        800: '#2f2da1',
        900: '#1e2481',
    },
    secondary: {
        50: '#e8edfa',
        100: '#bfcdf4',
        200: '#9eb3ee',
        300: '#6c8ce5',
        400: '#3a65dc',
        500: '#293bd9',
        600: '#2131ba',
        700: '#182488',
        800: '#121b66',
        900: '#0c1245',
    },
    success: {
        50: '#f0fff6',
        100: '#d1ffe4',
        200: '#a2ffc9',
        300: '#64ffa4',
        400: '#36ff89',
        500: '#00c853',
        600: '#00ab47',
        700: '#007832',
        800: '#005d27',
        900: '#00431c',
    },
    warning: {
        50: '#fffaf2',
        100: '#fff1d9',
        200: '#ffe4b3',
        300: '#ffd68c',
        400: '#ffc866',
        500: '#ffa400',
        600: '#df9000',
        700: '#bf7b00',
        800: '#9f6700',
        900: '#704800',
    },
    error: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d',
    },
    shades: {
        0: '#ffffff',
        100: '#000000',
    },
    shadows: {
        1: '0px 4px 8px 0px rgba(0, 0, 0, 0.1)',
        2: '0px 6px 12px 0px rgba(0, 0, 0, 0.1)',
        3: '0px 8px 16px 0px rgba(0, 0, 0, 0.1)',
        4: '0px 10px 20px 0px rgba(0, 0, 0, 0.1)',
        5: '0px 12px 24px 0px rgba(0, 0, 0, 0.1)',
    },
};

const theme = createTheme({
    breakpoints: {
        values: {
            mobile: 0,
            tablet: 768,
            desktop: 1024,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiButtonGroup: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
        },
        MuiCard: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                component: 'div',
            },
        },
        MuiDrawer: {
            defaultProps: {
                anchor: 'right',
                variant: 'temporary',
            },
        },
        MuiListSubheader: {
            defaultProps: {
                disableGutters: true,
                disableSticky: true,
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
        },
        MuiSelect: {
            defaultProps: {
                color: 'primary',
            },
        },
        MuiSkeleton: {
            defaultProps: {
                animation: 'wave',
            },
        },
        MuiSnackbar: {
            defaultProps: {
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            },
        },
        MuiStack: {
            defaultProps: {
                useFlexGap: true,
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: true,
                enterDelay: 0,
                placement: 'top',
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h2',
                    h3: 'h3',
                    h4: 'h4',
                    h5: 'h5',
                    h6: 'h6',
                    sh1: 'p',
                    sh2: 'p',
                    p1: 'p',
                    p2: 'p',
                    p3: 'p',
                    p4: 'p',
                },
            },
        },
        MuiUseMediaQuery: {
            defaultProps: {
                noSsr: true,
            },
        },
    },
    palette: {
        grey: {
            50: ensightfulPalette.neutral[50],
            100: ensightfulPalette.neutral[100],
            200: ensightfulPalette.neutral[200],
            300: ensightfulPalette.neutral[300],
            400: ensightfulPalette.neutral[400],
            500: ensightfulPalette.neutral[500],
            600: ensightfulPalette.neutral[600],
            700: ensightfulPalette.neutral[700],
            800: ensightfulPalette.neutral[800],
            900: ensightfulPalette.neutral[900],
        },
        primary: {
            main: ensightfulPalette.primary[500],
            light: ensightfulPalette.primary[400],
            dark: ensightfulPalette.primary[700],
        },
        secondary: {
            main: ensightfulPalette.secondary[500],
            light: ensightfulPalette.secondary[400],
            dark: ensightfulPalette.secondary[700],
        },
        ensightful: ensightfulPalette,
    },
    spacing: 4,
    transitions: {
        default: 'none',
    },
    typography: {
        h1: {
            fontSize: {
                small: 2.625,
                large: 3,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.04,
                large: -0.04,
            },
        },
        h2: {
            fontSize: {
                small: 2.25,
                large: 2.625,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.04,
                large: -0.04,
            },
        },
        h3: {
            fontSize: {
                small: 2,
                large: 2.25,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.02,
                large: -0.04,
            },
        },
        h4: {
            fontSize: {
                small: 1.75,
                large: 2,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.02,
                large: -0.02,
            },
        },
        h5: {
            fontSize: {
                small: 1.5,
                large: 1.75,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.02,
                large: -0.02,
            },
        },
        h6: {
            fontSize: {
                small: 1.25,
                large: 1.5,
            },
            fontWeight: {
                medium: 500,
                semibold: 600,
                bold: 700,
            },
            letterSpacing: {
                small: -0.02,
                large: -0.02,
            },
        },
        sh1: {
            fontSize: {
                small: 1.25,
                large: 1.25,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        sh2: {
            fontSize: {
                small: 1.125,
                large: 1.125,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        p1: {
            fontSize: {
                small: 1,
                large: 1,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        p2: {
            fontSize: {
                small: 0.875,
                large: 0.875,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        p3: {
            fontSize: {
                small: 0.75,
                large: 0.75,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        p4: {
            fontSize: {
                small: 0.625,
                large: 0.625,
            },
            fontWeight: {
                regular: 400,
                medium: 500,
                semibold: 600,
            },
            letterSpacing: {
                small: 0,
                large: 0,
            },
        },
        button: {
            fontFamily: 'inherit',
            fontSize: {
                small: 0.75,
                medium: 0.875,
                large: 1,
            },
            fontWeight: {
                contained: 600,
                outlined: 600,
                text: 500,
            },
            letterSpacing: 0,
            textTransform: 'none',
        },
        subtitle1: undefined,
        subtitle2: undefined,
        body1: undefined,
        body2: undefined,
        caption: undefined,
        overline: undefined,
        getLineHeight: (variant, size) => {
            const roundToClosestMultipleOfFour = (n) => {
                return Math.round(n / 4.0) * 4;
            };
            const pxLineHeight = roundToClosestMultipleOfFour(1.25 * 16 * theme.typography[variant].fontSize[size]);
            return pxLineHeight / 16;
        },
    },
});

export default theme;
